import { Action } from "redux";
import api from "../../api";
import { CurrentPageState } from ".";
import { CONTENT_TYPES, NavigationComposition } from "../../typings/document-types";
import { AppThunk } from "..";
import { logError } from "../../errors";

export const UPDATE_PAGE = "UPDATE_PAGE";

export interface UpdatePageAction extends Action
{
    type: typeof UPDATE_PAGE;
    page?: CONTENT_TYPES;
    redirectUrl?: string;
    statusCode: number;
}

export const updatePage = (statusCode: number, pageContent?: CONTENT_TYPES, redirectUrl?: string) =>
{
    const action: UpdatePageAction = {
        type: UPDATE_PAGE,
        statusCode: statusCode,
        page: pageContent,
        redirectUrl: redirectUrl,
    };
    return action;
};

export const showNotFoundPageAsync = (pathname: string): AppThunk => async (dispatch) =>
{
    try
    {
        const response = await api.umbracoClient.getByRoute("/landingpages/404");
        if (response.statusCode === 200)
        {
            dispatch(updatePage(404, {
                ...response.result,
                _url: pathname
            }));
            return;
        }
    }
    catch (error)
    {
        logError("Failed to fetch /404 page", error);
    }

    dispatch(updatePage(404, {
        _contentType: "errorPage",
        _name: "",
        _url: `${pathname}`,
        metaTitle: "404"
    }));
};

export const showErrorPageAsync = (pathname: string): AppThunk => async (dispatch) =>
{
    try
    {
        const response = await api.umbracoClient.getByRoute("/500");
        if (response.statusCode === 200)
        {
            dispatch(updatePage(404, response.result));
            return;
        }
    }
    catch (error)
    {
        logError("Failed to fetch /500 page", error);
    }

    dispatch(updatePage(501, {
        _contentType: "errorPage",
        _name: "",
        _url: `${pathname}`,
        metaTitle: "500"
    }));
};

export const thunkUpdatePageAsync = (pathOrUrl: string | undefined): AppThunk => async (dispatch, getState) =>
{
    if (pathOrUrl)
    {
        try
        {
            const response = await api.umbracoClient.getByRoute(pathOrUrl);

            switch (response.statusCode)
            {
                case 302:
                case 301: {
                    dispatch(updatePage(response.statusCode, response.result, response.redirectUrl));
                    break;
                }
                case 200: {
                    dispatch(updatePage(response.statusCode, response.result));
                    break;
                }
                case 404: {
                    dispatch(showNotFoundPageAsync(pathOrUrl));
                }
            }
        }
        catch (error)
        {
            logError("thunkUpdatePageAsync", error);
            dispatch(showErrorPageAsync(pathOrUrl));
        }
    }
};

export const updatePageByIdAsync = (pageId: number | string): AppThunk => async (dispatch, getState) =>
{
    const response = await api.umbracoClient.getById(pageId);
    dispatch(updatePage(response.statusCode, response.result));
};

export const getPageNavigationProps = (state: CurrentPageState): NavigationComposition | null =>
{
    return state.page as NavigationComposition || null;
};

export const getPageNavColor = (state: CurrentPageState): string =>
{
    return getPageNavigationProps(state)?.navDefaultColor || "rgb(0,0,0)";
};

export const getPageDropdownColor = (state: CurrentPageState): string =>
{
    return getPageNavigationProps(state)?.navDropdownColor || "rgb(255,255,255)";
};

export type PageActions = UpdatePageAction;